import {
  Box,
  InputAdornment,
  TextField,
  Typography,
  Button,
  Drawer,
  Divider,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import theme from "../theme";
import TicketCard from "./TicketCard";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import { ROLE, isHead, isRole, isServiceHead } from "../utils/roles";
import { useNavigate } from "react-router-dom";
import { STORE_ID } from "../Config/Constants";
import axios from "axios";
import LoadingScreen from "./LoadingScreen";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ReactComponent as SearchIcon } from "../assets/icons/search.svg";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import { ReactComponent as DoneIcon } from "../assets/icons/doneIcon.svg";

// ========== SprintCard Sub-Component ==========
const SprintCard = ({ goal, isMobile }) => {
  const [expanded, setExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState("20px");
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  const SINGLE_LINE_HEIGHT = 20;

  // Effect 1: check overflow when task_name changes (mirrors TicketCard's description effect)
  useEffect(() => {
    if (textRef.current) {
      const el = textRef.current;
      const isContentOverflowing = el.scrollHeight > SINGLE_LINE_HEIGHT;
      setIsOverflowing(isContentOverflowing);
      if (expanded) {
        setMaxHeight(`${el.scrollHeight}px`);
      } else {
        setMaxHeight(`${SINGLE_LINE_HEIGHT}px`);
      }
    }
  }, [goal.task_name]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2: animate height on expand/collapse (mirrors TicketCard's expandedCard effect)
  useEffect(() => {
    if (textRef.current) {
      const el = textRef.current;
      if (expanded) {
        setMaxHeight(`${el.scrollHeight}px`);
      } else {
        setMaxHeight(`${SINGLE_LINE_HEIGHT}px`);
      }
    }
  }, [expanded]);

  return (
    <Box
      onClick={() => {
        if (isOverflowing) setExpanded((prev) => !prev);
      }}
      sx={{
        width: "100%",
        minHeight: 36,
        borderRadius: 2,
        border: "1px solid rgba(193,193,193,0.8)",
        bgcolor: "#fff",
        userSelect: "none",
        cursor: isOverflowing ? "pointer" : "default",
        boxSizing: "border-box",
        px: 1.5,
        py: 0.7,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1,
      }}
    >
      {/* Task name with maxHeight animation — identical to TicketCard description */}
      <Box
        ref={textRef}
        sx={{
          flex: 1,
          fontSize: 12,
          fontWeight: 500,
          color: "rgba(50,50,70,0.9)",
          lineHeight: `${SINGLE_LINE_HEIGHT}px`,
          overflow: "hidden",
          maxHeight: maxHeight,
          transition: "max-height 0.5s ease",
          wordBreak: "break-word",
          whiteSpace: "normal",
        }}
      >
        {goal.task_name}
      </Box>

      {/* Right: target badge + chevron */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            px: 0.9,
            py: 0.15,
            borderRadius: 1,
            bgcolor: "rgba(0,128,128,0.08)",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: "rgba(0,100,100,1)",
              whiteSpace: "nowrap",
            }}
          >
            {goal.target}
          </Typography>
        </Box>

        {isOverflowing && (
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              color: "rgba(130,130,150,0.8)",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.3s ease",
            }}
          />
        )}
      </Box>
    </Box>
  );
};

// ========== SprintGoals Sub-Component ==========
const SprintGoals = ({ sprintGoals, isMobile }) => {
  return (
    <Box sx={{ mt: 1.5, mb: 0.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: "rgba(0,128,128,1)",
          letterSpacing: 0.5,
          textTransform: "uppercase",
          mb: 0.8,
        }}
      >
        Sprint
      </Typography>

      {/* Exactly the same grid as the ticket list below */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: isMobile ? "8px" : "0 10px",
          width: "100%",
        }}
      >
        {sprintGoals.map((goal) => (
          <Box
            key={goal.task_id}
            sx={{
              width: "100%",
              py: !isMobile ? 0.8 : 0,
              px: !isMobile ? 0.4 : 0,
            }}
          >
            <SprintCard goal={goal} isMobile={isMobile} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const HomePage = () => {
  // ========== Hooks & Context ==========
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));
  const navigate = useNavigate();

  // Context
  const { userInfo, userGoals, allKumuduDepartments, allInternalDepartments } =
    useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;
  const role = userInfo?.role;

  // ========== State ==========
  const [allTickets, setAllTickets] = useState(null); // Stores all fetched tickets
  const [loading, setLoading] = useState(true); // Loader state
  const [error, setError] = useState(false); // Error state

  const [page, setPage] = useState(1); // Pagination state
  const rowsPerPage = isMobile ? 10 : 15; // Dynamic page size based on screen

  // Default filters (depends on store)
  const DEFAULT_FILTERS = {
    status:
      store_id === STORE_ID
        ? ["open"] // For this specific store, only open tickets
        : ["open", "in_progress"],
    priority: [],
    date: "",
  };

  // Filters (with persistence in localStorage)
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem("filters");
    return saved ? JSON.parse(saved) : DEFAULT_FILTERS;
  });

  // Sorting, search, and drawer states
  const [sortBy, setSortBy] = useState("earliest");
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState(() => {
    return localStorage.getItem("ticketSearchHome") || "";
  });
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [resolvedToggle, setResolvedToggle] = useState(() => {
    const saved = localStorage.getItem("resolvedToggle");
    return saved ? JSON.parse(saved) : false; // default false
  });

  // ========== Options ==========
  const statusOptions = [
    "open",
    "in_progress",
    "resolved",
    "rejected",
    "follow_up",
  ];
  const priorityOptions = ["high", "medium", "low"];

  // Status label mapping
  const STATUS_LABELS = {
    assigned_tickets: "Assigned to",
    open: "Open",
    in_progress: "In Progress",
    resolved: "Resolved",
    rejected: "Rejected",
    follow_up: "Follow Up",
  };

  // ========== State: Sprint Goals ==========
  const [sprintGoals, setSprintGoals] = useState([]);

  // ========== Handlers ==========
  // Toggle filter values (add/remove)
  const toggleFilter = (field, value) => {
    setFilters((prev) => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter((v) => v !== value) };
      }
      return { ...prev, [field]: [...current, value] };
    });
  };

  // Clear all filters and reset to default
  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    localStorage.setItem("filters", JSON.stringify(DEFAULT_FILTERS));
  };

  // ========== Effects ==========
  // Persist filters to localStorage
  useEffect(() => {
    localStorage.setItem("filters", JSON.stringify(filters));
    localStorage.setItem("resolvedToggle", JSON.stringify(resolvedToggle));
  }, [filters, resolvedToggle]);

  // Load saved filters on first render
  useEffect(() => {
    const savedFilters = localStorage.getItem("filters");
    const savedToggle = localStorage.getItem("resolvedToggle");

    if (savedFilters) setFilters(JSON.parse(savedFilters));
    if (savedToggle !== null) setResolvedToggle(JSON.parse(savedToggle));
  }, []);

  // Fetch tickets based on user role & store
  useEffect(() => {
    const handleFetchTickets = async () => {
      if (store_id === undefined || store_id === null) {
        return;
      }
      setLoading(true);
      try {
        let url;

        // Different endpoints based on store and role
        if (store_id === STORE_ID) {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/getAllTickets`;
          if (isServiceHead(role) || isRole(role, ROLE.SERVICE_STAFF)) {
            url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/getAllTickets`;
          }
        } else {
          url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/getTicketsByStore/${store_id}`;
        }

        const res = await axios.get(url);
        const allTickets = res?.data?.data || [];
        const allTicketsPerDepartment =
          allTickets.filter((ticket) => ticket.dept_id === userInfo.dept_id) ||
          [];
        isHead(userInfo.role)
          ? setAllTickets(allTickets)
          : setAllTickets(allTicketsPerDepartment);
      } catch (error) {
        console.error("API error:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    handleFetchTickets();
    // eslint-disable-next-line
  }, [store_id]);

  // Fetch sprint goals for current user
  useEffect(() => {
    const fetchSprintGoals = async () => {
      if (!userInfo?.id) return;
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/ticket/goals/${userInfo.id}`,
        );
        const goals = res?.data?.data || [];
        setSprintGoals(
          goals.filter((g) => g.task_type?.toLowerCase() === "sprint"),
        );
      } catch (err) {
        console.error("Failed to fetch sprint goals:", err);
      }
    };
    fetchSprintGoals();
    // eslint-disable-next-line
  }, [userInfo?.id]);

  // ========== Derived Data ==========
  // Filtered tickets (status, priority, date, search)
  let filteredTickets = [];

  if (Array.isArray(allTickets)) {
    if (searchText.trim() !== "") {
      // 🔍 Direct search from all tickets (ignore filters)
      const s = searchText.toLowerCase();
      filteredTickets = allTickets.filter((ticket) => {
        const fieldsToSearch = [
          ticket.ticket_number?.toString().toLowerCase(),
          ticket.department_name?.toLowerCase(),
          ticket.assigned_to_name?.toLowerCase(),
          ticket.store_id?.toString().toLowerCase(),
          ticket.store_name?.toLowerCase(),
        ];
        return fieldsToSearch.some((field) => field?.includes(s));
      });
    } else {
      // 🧩 Apply filters only when not searching
      filteredTickets = allTickets
        .filter((ticket) => {
          if (
            filters.status.length > 0 &&
            !filters.status.includes(ticket.status)
          )
            return false;

          if (
            filters.priority.length > 0 &&
            !filters.priority.includes(ticket.priority)
          )
            return false;

          if (filters.date) {
            const filterDate = new Date(filters.date);
            let ticketDate = new Date(ticket.start_date);
            if (
              filters.status.length === 1 &&
              filters.status[0] === "resolved"
            ) {
              ticketDate = new Date(ticket.updated_at);
            }
            if (ticketDate < filterDate) return false;
          }

          return true;
        })
        .sort((a, b) => {
          if (resolvedToggle) {
            return new Date(b.updated_at) - new Date(a.updated_at);
          }
          return new Date(b.start_date) - new Date(a.start_date);
        });
    }
  }

  // Pagination logic
  const totalPages = Math.ceil(filteredTickets.length / rowsPerPage);
  const paginatedTickets = Array.isArray(filteredTickets)
    ? filteredTickets.slice((page - 1) * rowsPerPage, page * rowsPerPage)
    : [];

  const handleResolveClick = () => {
    setResolvedToggle((prev) => {
      const newValue = !prev;

      if (newValue) {
        // ✅ Show only resolved tickets
        setFilters((prevFilters) => ({
          ...prevFilters,
          status: ["resolved"],
        }));
      } else {
        // ✅ Reset to default filters
        setFilters(DEFAULT_FILTERS);
      }

      return newValue;
    });
  };

  return (
    <>
      <Box>
        {/* Search & Filter Section */}
        <Box
          sx={{
            bgcolor: "#fff",
            width: isMobile ? "100vw" : "100%",
            marginX: isMobile ? -2 : 0,
            pb: 1,
            paddingLeft: isMobile ? 2 : 0,
          }}
        >
          <Grid
            container
            spacing={1}
            sx={{ mt: isMobile ? 2 : 1, flexWrap: "nowrap" }}
          >
            {/* Search box */}
            <Grid item xs={isMobile ? 9 : 8}>
              <TextField
                fullWidth
                placeholder="Search Tickets "
                variant="outlined"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                multiline
                minRows={1}
                maxRows={4}
                sx={formStyle}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Default Filter Button */}
            <Grid item xs={isMobile ? 2.5 : 2}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  width: "100%",
                  height: "44px",
                  border: `1px solid rgba(193,193,193)`,
                  borderRadius: 2,
                  cursor: "pointer",
                }}
                onClick={() => setFilterDrawerOpen(true)}
              >
                <FilterListRoundedIcon
                  sx={{ fontSize: 22, fill: "rgba(132, 128, 128, 1)" }}
                />
                {!isMobile && (
                  <Typography sx={{ color: "gray" }}>Filter</Typography>
                )}
              </Box>
            </Grid>

            {/* Extra Filter Button if store_id !== STORE_ID */}
            {store_id !== STORE_ID && (
              <Grid item xs={isMobile ? 2.5 : 2}>
                <Box
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    width: "100%",
                    height: "44px",
                    color: resolvedToggle ? "#fff" : "#0E830A",
                    border: "1px solid #0E830A",
                    bgcolor: resolvedToggle ? "#86c284ff" : "#F5FFF4",
                    borderRadius: 2,
                  }}
                  onClick={handleResolveClick}
                >
                  <DoneIcon style={{ width: "24px", height: "24px" }} />
                  {!isMobile && (
                    <Typography sx={{ fontSize: 14 }}>Resolved</Typography>
                  )}
                </Box>
              </Grid>
            )}

            {/* Add Ticket Button - only on desktop */}
            {!isMobile && (
              <Grid item xs={2}>
                <Box
                  sx={{
                    gap: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "44px",
                    borderRadius: 2,
                    cursor: "pointer",
                    color: "#fff",
                    background:
                      "linear-gradient(90deg,rgba(0, 128, 128, 1) 0%, rgba(68, 40, 160, 1) 100%)",
                    boxShadow: "0px 3px 8px rgba(0,0,0,0.2)",
                  }}
                  onClick={() => navigate("/all-service-tickets")}
                >
                  <Typography sx={{ fontWeight: 400, fontSize: 14 }}>
                    Add Ticket
                  </Typography>
                  <AddIcon />
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* ========== Sprint Goals ========== */}
        {sprintGoals.length > 0 && (
          <SprintGoals sprintGoals={sprintGoals} isMobile={isMobile} />
        )}
      </Box>

      {/* Ticket List */}
      <Box
        sx={{
          mt: 1,
          mb: 0,
          mx: "auto",
          width: "100%",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", // 1 column for mobile, 3 for web
          gap: isMobile ? "8px" : "0 10px", // spacing between rows/columns
        }}
      >
        {loading ? (
          <LoadingScreen loadingMessage={"Loading tickets"} />
        ) : error ? (
          <Typography
            color={theme.textCol.subHeading}
            sx={{
              textAlign: "center",
              mt: 5,
              opacity: 0.8,
              gridColumn: "1 / -1", // span full width
            }}
          >
            Something went wrong while loading tickets.
          </Typography>
        ) : filteredTickets.length > 0 ? (
          <>
            {paginatedTickets.map((ticket, index) => (
              <Box
                key={`${ticket.id}-${index}`}
                sx={{
                  borderRadius: 1,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  py: !isMobile ? 0.8 : 0,
                  px: !isMobile ? 0.4 : 0,
                }}
              >
                <TicketCard ticket={ticket} />
              </Box>
            ))}

            {/* Pagination */}
            {filteredTickets.length > rowsPerPage && (
              <Box
                sx={{
                  mt: isMobile ? 1 : 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  gridColumn: "1 / -1", // span full width
                }}
              >
                <Button
                  size="small"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  sx={{
                    color: theme.textCol.primaryHeading,
                    minWidth: 36,
                    fontSize: 14,
                    fontWeight: 400,
                    textTransform: "none",
                    "&:hover": { backgroundColor: "none" },
                  }}
                >
                  <ChevronLeftRoundedIcon /> {"Previous"}
                </Button>

                <Button
                  size="small"
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={page === totalPages}
                  sx={{
                    color: theme.textCol.primaryHeading,
                    minWidth: 36,
                    fontSize: 14,
                    fontWeight: 400,
                    textTransform: "none",
                    "&:hover": { backgroundColor: "none" },
                  }}
                >
                  {"Next"} <ChevronRightRoundedIcon />
                </Button>
              </Box>
            )}
          </>
        ) : (
          <Typography
            sx={{
              textAlign: "center",
              mt: 5,
              fontSize: 16,
              color: theme.textCol.subHeading,
              gridColumn: "1 / -1", // span full width
            }}
          >
            No ticket Found!.
          </Typography>
        )}
      </Box>

      {/* Compact Filter Drawer */}
      <Drawer
        anchor="bottom"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            zIndex: 99999,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            p: 2,
            width: "100%",
            maxHeight: "60vh",
            overflowY: "auto",
            pb: "calc(56px + env(safe-area-inset-bottom, 44px))",
          },
        }}
        ModalProps={{
          keepMounted: true,
          sx: { zIndex: 99999 },
          BackdropProps: { sx: { zIndex: 99998 } },
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="subtitle1" fontWeight={700} fontSize={16}>
            Filters
          </Typography>
          <IconButton size="small" onClick={() => setFilterDrawerOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Date */}
        <Typography variant="body2" fontWeight={600} mb={0.5}>
          From Date
        </Typography>
        <TextField
          type="date"
          fullWidth
          size="small"
          value={filters.date}
          onChange={(e) =>
            setFilters((prev) => ({ ...prev, date: e.target.value }))
          }
          sx={{ mb: 1 }}
        />
        <Divider sx={{ mb: 1 }} />
        {/* Status buttons */}
        <Typography variant="body2" fontWeight={600} mb={0.5}>
          Status
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
          {statusOptions.map((status) => {
            const selected = filters.status.includes(status);
            return (
              <Button
                key={status}
                variant={selected ? "contained" : "outlined"}
                onClick={() => toggleFilter("status", status)}
                sx={{
                  borderRadius: "50px", // capsule shape
                  textTransform: "none",
                  px: 2,
                  py: 0.5,
                  fontSize: 12,
                  fontWeight: selected ? 400 : 300,
                  backgroundColor: selected
                    ? theme.lightTheme.themeCol
                    : "#fff",
                  color: selected ? "#fff" : "gray",
                  borderColor: selected ? theme.lightTheme.themeCol : "gray",
                  "&:hover": {
                    backgroundColor: selected
                      ? theme.lightTheme.themeCol
                      : "#f0f0f0",
                  },
                }}
              >
                {status.replace(/_/g, " ").charAt(0).toUpperCase() +
                  status.replace(/_/g, " ").slice(1)}
              </Button>
            );
          })}
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Priority buttons */}
        <Typography variant="body2" fontWeight={600} mb={0.5}>
          Priority
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
          {priorityOptions.map((priority) => {
            const selected = filters.priority.includes(priority);
            return (
              <Button
                key={priority}
                variant={selected ? "contained" : "outlined"}
                onClick={() => toggleFilter("priority", priority)}
                sx={{
                  borderRadius: "50px", // capsule shape
                  textTransform: "none",
                  px: 2,
                  py: 0.5,
                  fontSize: 12,
                  fontWeight: selected ? 400 : 300,
                  backgroundColor: selected
                    ? theme.lightTheme.themeCol
                    : "#fff",
                  color: selected ? "#fff" : "gray",
                  borderColor: selected ? theme.lightTheme.themeCol : "gray",
                  "&:hover": {
                    backgroundColor: selected
                      ? theme.lightTheme.themeCol
                      : "#f0f0f0",
                  },
                }}
              >
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </Button>
            );
          })}
        </Box>

        <Divider sx={{ mb: 1 }} />

        {/* Buttons */}
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button
            size="small"
            sx={{
              border: `2px solid ${theme.lightTheme.themeCol}`,
              color: theme.lightTheme.themeCol,
            }}
            onClick={clearFilters}
          >
            Default
          </Button>
          <Button
            size="small"
            sx={{
              border: `2px solid ${theme.lightTheme.themeCol}`,
              backgroundColor: theme.lightTheme.themeCol,
              color: "#fff",
            }}
            onClick={() => setFilterDrawerOpen(false)}
          >
            Apply
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

const formStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "50px",
    height: "44px",
    fontSize: "14px",
    px: 3,
    "& fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(193,193,193)",
      borderRadius: "50px",
    },
    "&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(193,193,193)",
    },
    "&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(193,193,193)",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "rgba(193,193,193)",
  },
};

export default HomePage;
